/**
 * InstallPromptButton — Iter266
 *
 * Cross-platform "Instalar AgenteLead" button:
 *   • Android / desktop Chrome / Edge → listens to `beforeinstallprompt`
 *     and triggers the native install dialog on click.
 *   • iOS Safari → shows a compact instruction popover ("Compartilhar →
 *     Adicionar à Tela de Início") since Apple doesn't fire the event.
 *   • Already-installed (standalone display-mode) → hides itself.
 *
 * Renders nothing when the browser can't install (e.g. already installed
 * or unsupported). Safe to drop-in anywhere.
 *
 * Props:
 *   • variant — "primary" (default, gold pill) | "chip" (small, header-safe)
 *   • className — override the wrapper class
 */
import { useEffect, useState, useCallback } from "react";
import { Download, Share, Plus, X, Check } from "lucide-react";

function _isIOSStandalone() {
  return typeof window !== "undefined"
    && (window.matchMedia("(display-mode: standalone)").matches
        || window.navigator.standalone === true);
}

function _isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // iPad on iOS 13+ reports as Mac — extra check via touch points.
  return /iPad|iPhone|iPod/.test(ua)
    || (ua.includes("Mac") && "ontouchend" in document);
}

export default function InstallPromptButton({ variant = "primary", className = "" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  const ios = _isIOS();
  const standalone = _isIOSStandalone();

  // Capture the beforeinstallprompt event (Chromium / Edge / Firefox mobile).
  useEffect(() => {
    if (standalone) { setInstalled(true); return; }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [standalone]);

  const handleClick = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferredPrompt(null);
      return;
    }
    if (ios) setShowIOSHelp(true);
  }, [deferredPrompt, ios]);

  // Nothing to show: already installed OR neither Android-prompt nor iOS.
  if (installed) return null;
  if (!deferredPrompt && !ios) return null;

  const isChip = variant === "chip";
  const btnBase = isChip
    ? "h-8 px-2.5 rounded-lg text-[11px]"
    : "h-9 sm:h-10 px-3.5 rounded-xl text-[12.5px]";
  const btnStyle = {
    background: "linear-gradient(135deg,#C9A24E 0%,#8A6A2A 100%)",
    color: "#0A1233",
    boxShadow: "0 4px 12px rgba(201,162,78,0.25)",
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="pwa-install-btn"
        className={`inline-flex items-center gap-1.5 font-black tracking-tight hover:brightness-110 active:scale-95 transition ${btnBase} ${className}`}
        style={btnStyle}
        title={ios ? "Instalar no iPhone" : "Instalar AgenteLead como app"}
      >
        <Download className={isChip ? "w-3 h-3" : "w-3.5 h-3.5"} />
        {isChip ? "Instalar" : "Instalar app"}
      </button>

      {showIOSHelp && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4"
             data-testid="pwa-ios-help-overlay"
             style={{ background: "rgba(2,6,23,0.78)", backdropFilter: "blur(6px)" }}
             onClick={() => setShowIOSHelp(false)}>
          <div className="w-full max-w-md rounded-2xl border shadow-2xl mobile-sheet overflow-hidden"
               style={{ background: "#FFFFFF", borderColor: "#E5E2DA" }}
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-3 flex items-start justify-between">
              <div>
                <div className="text-[10.5px] font-black uppercase tracking-widest text-[#C9A24E]">
                  Instalar no iPhone
                </div>
                <h3 className="text-[18px] font-black text-[#0A1233] mt-1">
                  Adicionar AgenteLead à Tela de Início
                </h3>
              </div>
              <button onClick={() => setShowIOSHelp(false)}
                      data-testid="pwa-ios-help-close"
                      className="w-10 h-10 -mr-2 rounded-full hover:bg-slate-100 text-slate-500 flex items-center justify-center touch-target">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-3">
              <ol className="space-y-2.5 text-[13.5px] text-slate-700">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#0A1233] text-white text-[11px] font-black flex items-center justify-center shrink-0">1</span>
                  <span>
                    Toque em <b className="text-[#0A1233]">Compartilhar</b>{" "}
                    <Share className="inline w-3.5 h-3.5 -mt-0.5 text-[#0A1233]" />{" "}
                    na barra do Safari (abaixo)
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#0A1233] text-white text-[11px] font-black flex items-center justify-center shrink-0">2</span>
                  <span>
                    Role até <b className="text-[#0A1233]">Adicionar à Tela de Início</b>{" "}
                    <Plus className="inline w-3.5 h-3.5 -mt-0.5 text-[#0A1233]" />
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white text-[11px] font-black flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3" />
                  </span>
                  <span>
                    Toque em <b className="text-[#0A1233]">Adicionar</b> — o ícone AgenteLead aparece na Home
                  </span>
                </li>
              </ol>

              <div className="rounded-xl border p-3 flex items-center gap-3"
                   style={{ background: "#F5F2EA", borderColor: "#E5E2DA" }}>
                <img src="/icons/icon-152.png" alt=""
                     className="w-11 h-11 rounded-xl shadow-sm" />
                <div>
                  <div className="text-[12.5px] font-black text-[#0A1233]">AgenteLead</div>
                  <div className="text-[11px] text-slate-500">Cold-emails, prospects e alertas — em tela cheia sem barra do browser</div>
                </div>
              </div>

              <button type="button"
                onClick={() => setShowIOSHelp(false)}
                data-testid="pwa-ios-help-dismiss"
                className="w-full h-11 rounded-xl font-black text-[13.5px] text-[#0A1233]"
                style={{ background: "linear-gradient(135deg,#C9A24E 0%,#8A6A2A 100%)" }}>
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
